import { flatten } from 'flat';
import _ from 'lodash';

export type ValidationSchemaType<T> = {
  [K in keyof T]: NonNullable<T[K]> extends (infer R)[]
  ? (value: R[], values?: T) => string | undefined
  : NonNullable<T[K]> extends object
  ? ((value: T[K], values?: T) => string | undefined) | ValidationSchemaType<T[K]>
  : (value: T[K], values?: T) => string | undefined;
};

export type FlatValidationSchema<T> = {
  [K in keyof T]: (value: T[K], values: T) => string | undefined;
};

export type FlatValidationErrors<T> = {
  [K in keyof T]?: string | undefined;
};

export type ValidationErrors<T> = {
  [K in keyof T]: NonNullable<T[K]> extends (infer R)[]
  ? string
  : NonNullable<T[K]> extends object
  ? ValidationErrors<T[K]>
  : string;
};

export const check = <T, R>(...checker: ((value: T, values?: R) => string | undefined)[]) => (
  value: T,
  values?: R
) => {
  const c = _.find(checker, (check) => check(value, values));
  return c ? c(value, values) : undefined;
};

export const needsCheck = (isNeeded: boolean) => <T>(
  ...checker: ((value: T) => string | undefined)[]
) => {
  return isNeeded ? check(...checker) : undefined;
};

export const createValidationSchema = <T>(schema: ValidationSchemaType<T>) => (
  value: T
) => {
  const flattenSchema: FlatValidationSchema<T> = flatten(schema);

  let errors: FlatValidationErrors<T> = {};
  _.forEach(
    Object.keys(flattenSchema) as (keyof FlatValidationSchema<T>)[],
    (key) => {
      errors[key] = flattenSchema[key]
        ? flattenSchema[key](_.get(value, key), value)
        : undefined;
    }
  );
  return removeUndefinedFromObject(errors) as FlatValidationErrors<T>;
};

const removeUndefinedFromObject = (obj: object) =>
  Object.fromEntries(Object.entries(obj).filter(([k, v]) => v !== undefined));
